import React, { useState } from 'react';
import { Modal, Upload, Button, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useTranslation } from '@refinedev/core';
import type { UploadProps } from 'antd';
import useApi from '@/api/useApi';
import { endPoints } from '@/api/configs';

const { Dragger } = Upload;

interface BulkAddAchievementsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const BulkAddAchievementsModal: React.FC<
  BulkAddAchievementsModalProps
> = ({ isOpen, onClose, onSuccess }) => {
  const { translate } = useTranslation();
  const [fileList, setFileList] = useState<any[]>([]);
  const { usePost } = useApi();
  const { mutate: uploadUsers, isLoading } = usePost<Blob, FormData>(
    endPoints.BulkAddAchievements,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    }
  );
  const uploadProps: UploadProps = {
    accept: '.xlsx, .xls',
    maxCount: 1,
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList?.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      const isExcel =
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel';
      if (!isExcel) {
        message.error(translate('bulk-create-achievements.excel-files-only'));
        return false;
      }
      setFileList([file]);
      return false;
    },
    fileList,
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error(translate('bulk-create-achievements.please-select-a-file'));
      return;
    }

    const formData = new FormData();
    formData.append('file', fileList[0]);
    uploadUsers(formData, {
      onSuccess: (response) => {
        setFileList([]);
        message.success(
          translate('bulk-create-achievements.uploaded-successfully')
        );

        // Get the blob from the response
        const blob = response.data;
        if (
          blob instanceof Blob &&
          blob.type ===
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ) {
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;

          // Get filename from response headers
          const contentDisposition = response.headers?.['content-disposition'];
          const filename = contentDisposition
            ? contentDisposition.split('filename=')[1].replace(/"/g, '')
            : 'bulk-signup-errors.xlsx';

          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        }

        onSuccess?.();
        onClose();
      },
      onError: () => {
        message.error(translate('bulk-create-achievements.upload-failed'));
      },
    });
  };

  return (
    <Modal
      title={translate('bulk-create-achievements.title')}
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          {translate('buttons.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isLoading}
          onClick={handleUpload}
        >
          {translate('buttons.upload')}
        </Button>,
      ]}
      onClose={onClose}
    >
      <Dragger {...uploadProps}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          {translate('bulk-create-achievements.click-or-drag')}
        </p>
        <p className="ant-upload-hint">
          {translate('bulk-create-achievements.excel-files-only')}
        </p>
      </Dragger>
    </Modal>
  );
};
